import { ChartColumn } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { IgButton } from "@intelligems/ig-theme/IgButton";
import { IgPaper } from "@intelligems/ig-theme/IgPaper";
import { IgTypography } from "@intelligems/ig-theme/IgTypography";
import { Stack, useTheme } from "@mui/material";
import { SETTINGS_ROUTES } from "@src/routes/routes";
import { pylonChangePlanRequest } from "@src/utils/linkSheet";
import { openNewTab } from "@src/utils/openNewTab";

interface AnalyticsAccessDeniedProps {
  description?: string;
  showContactSupportButton?: boolean;
  showUpgradeButton?: boolean;
  title?: string;
}

export const AnalyticsAccessDenied = ({
  title,
  description,
  showUpgradeButton = true,
  showContactSupportButton = true,
}: AnalyticsAccessDeniedProps) => {
  const theme = useTheme();
  const navigate = useNavigate();

  const defaultTitle = "Analytics Access Restricted";
  const defaultDescription =
    "Your current plan does not include access to analytics. Upgrade your plan to unlock detailed performance insights.";

  const handleUpgradeClick = () => {
    void navigate(`${SETTINGS_ROUTES.Settings}#billing`);
  };

  const handleContact = () => {
    openNewTab(pylonChangePlanRequest);
  };

  return (
    <IgPaper>
      <Stack
        sx={{
          alignItems: "center",
          justifyContent: "center",
          gap: 3,
          py: 15,
          px: 4,
          maxWidth: 560,
          mx: "auto",
        }}
      >
        <ChartColumn
          size={50}
          color={theme.palette.text.secondary}
          strokeWidth={1.5}
        />

        <IgTypography variant="h1" align="center" fontWeight={600}>
          {title || defaultTitle}
        </IgTypography>

        <IgTypography align="center" color={theme.palette.text.secondary}>
          {description || defaultDescription}
        </IgTypography>

        {(showUpgradeButton || showContactSupportButton) && (
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            sx={{ mt: 4 }}
          >
            {showContactSupportButton && (
              <IgButton variant="outlined" onClick={handleContact}>
                Contact Sales
              </IgButton>
            )}

            {showUpgradeButton && (
              <IgButton variant="contained" onClick={handleUpgradeClick}>
                Upgrade Plan
              </IgButton>
            )}
          </Stack>
        )}
      </Stack>
    </IgPaper>
  );
};
